/**
 * TimelineTaskTrackerSuccessionLayer Component
 *
 * Mirrors TimelineTaskTrackerLayer but for succession child rows.
 * Only renders hover tracker + bubble + submission badges when the parent
 * crop is expanded — collapsed succession rows are too dense for interaction.
 */

import React, { useState, useCallback, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { Button } from '@mui/material'
import { useTimelineStaticContext } from '../../context/timeline-static-context'
import { useTimelineStyles } from '../../../hooks/use-timeline-styles'
import {
  SUCCESSION_EXPANDED_HEIGHT,
  SUCCESSION_THIN_HEIGHT,
  SUCCESSION_THIN_GAP,
  VERTICAL_DAY_TRACKER_SPACE,
  EXPANDED_ROW_HEIGHT,
} from '../../timeline-constants'
import { TimelineTaskSubmissionPopper } from './timeline-task-submission-popper'
import { TimelinePopsicleBadge, POPSICLE_BADGE_R } from '../content/timeline-popsicle-badge'
import { ControlPointDuplicate } from '@mui/icons-material'
import { useFeatures } from '~/common/use-features'

const BUBBLE_R = 10
const BUBBLE_TOP_GAP = 5

/**
 * @param {Object} props
 * @param {Array} props.crops - Transformed crop data (with succession.children[].tasks)
 */
export const TimelineTaskTrackerSuccessionLayer = React.memo(({ crops, hideBadges = false }) => {
  const {
    expandedIds,
    weekWidth,
    getRowY,
    forceAllExpand,
    viewStrategy,
    showSuccessions,
    contentRef,
    readOnly,
    collapsedRowHeight,
    collapsedRowHeightGap,
  } = useTimelineStaticContext()

  const { getTaskStyle } = useTimelineStyles()
  const { features } = useFeatures()

  const [hoveredTracker, setHoveredTracker] = useState(null) // { taskId, x }
  const [popperState, setPopperState] = useState(null)
  const [portalTarget, setPortalTarget] = useState(null)

  useEffect(() => {
    setPortalTarget(contentRef.current)
  }, [contentRef])

  const handleMouseMove = useCallback((e, taskId, rectX, rectWidth) => {
    const containerRect = contentRef.current.getBoundingClientRect()
    const rawX = e.clientX - containerRect.left + contentRef.current.scrollLeft
    const clampedX = Math.max(rectX, Math.min(rectX + rectWidth, rawX))
    setHoveredTracker({ taskId, x: clampedX })
  }, [contentRef])

  const handleMouseLeave = useCallback((taskId) => {
    setHoveredTracker(prev => (prev?.taskId === taskId ? null : prev))
  }, [])

  const handleBubbleClick = useCallback((e, task) => {
    if (readOnly) return
    e.stopPropagation()
    setPopperState({ open: true, task, anchorX: e.clientX, anchorY: e.clientY })
  }, [readOnly])

  const handlePopperClose = useCallback(() => {
    setPopperState(null)
  }, [])

  if (!showSuccessions) return null

  const heightMultiplier = viewStrategy === 'both' ? 2 : 1
  const showTasks = viewStrategy === 'tasks' || viewStrategy === 'both'

  return (
    <>
      {crops.map((crop) => {
        if (crop.isOrgTaskRow) return null
        const succession = crop.succession
        if (!succession?.count || succession.count < 1) return null

        const isMainExpanded = forceAllExpand || expandedIds.includes(crop.id)

        const baseMainRowH = isMainExpanded ? EXPANDED_ROW_HEIGHT : collapsedRowHeight
        const mainRowH = viewStrategy === 'both' ? baseMainRowH * 2 : baseMainRowH
        const mainRowY = getRowY(crop.id) + VERTICAL_DAY_TRACKER_SPACE * 8
        const successionBaseY = mainRowY + mainRowH

        const succRowH = isMainExpanded
          ? SUCCESSION_EXPANDED_HEIGHT * heightMultiplier
          : SUCCESSION_THIN_HEIGHT

        const barH = isMainExpanded
          ? SUCCESSION_EXPANDED_HEIGHT - collapsedRowHeightGap
          : SUCCESSION_THIN_HEIGHT

        const subRowH = isMainExpanded ? SUCCESSION_EXPANDED_HEIGHT : SUCCESSION_THIN_HEIGHT

        const rowStep = isMainExpanded
          ? SUCCESSION_EXPANDED_HEIGHT * heightMultiplier
          : SUCCESSION_THIN_HEIGHT + SUCCESSION_THIN_GAP

        return (
          <g key={`succ-tracker-${crop.id}`}>
            {succession.children.map((child, i) => {
              if (!showTasks) return null

              const succRowY = successionBaseY + i * rowStep
              const y = viewStrategy === 'both' && isMainExpanded
                ? succRowY + subRowH + (subRowH / 2) - (barH / 2)
                : succRowY + (succRowH / 2) - (barH / 2)

              const multiSubmitTasks = child.tasks?.filter(t => t.single_completion === false) ?? []

              return (
                <g key={`succ-tracker-child-${child.id ?? i}`}>
                  {multiSubmitTasks.map((task) => {
                    const rectX = task.start * weekWidth
                    const rectWidth = task.len * weekWidth
                    const isHovered = hoveredTracker?.taskId === task.id
                    const trackerX = isHovered ? hoveredTracker.x : null
                    const taskStyle = getTaskStyle(task.type)
                    const taskColor = taskStyle.color
                    const taskIcon = taskStyle.icon

                    return (
                      <g
                        key={`succ-task-tracker-${task.id}`}
                        onMouseLeave={() => handleMouseLeave(task.id)}
                      >
                        {/* Invisible capture rect — only active when expanded */}
                        {isMainExpanded && (
                          <rect
                            x={rectX}
                            y={y}
                            width={rectWidth}
                            height={barH}
                            fill="transparent"
                            onMouseMove={(e) => handleMouseMove(e, task.id, rectX, rectWidth)}
                          />
                        )}

                        {/* Tracker line — only when expanded and hovered */}
                        {isMainExpanded && isHovered && trackerX !== null && (
                          <g style={{ pointerEvents: 'none' }}>
                            <line
                              x1={trackerX}
                              y1={y}
                              x2={trackerX}
                              y2={y + barH}
                              stroke={taskColor}
                              strokeWidth={1.5}
                              strokeDasharray="3,2"
                              opacity={0.9}
                            />
                          </g>
                        )}

                        {/* Submission badges — only when expanded */}
                        {isMainExpanded && !hideBadges && task.submissions?.map((sub) => {
                          const cx = Math.max(
                            rectX + POPSICLE_BADGE_R + 2,
                            Math.min(rectX + rectWidth - POPSICLE_BADGE_R - 2, sub.submitted_at_weeks * weekWidth)
                          )
                          return (
                            <TimelinePopsicleBadge
                              key={sub.id}
                              cx={cx}
                              y={y}
                              color={taskColor}
                              icon={taskIcon}
                              onClick={(e) => {
                                e.stopPropagation()
                                setPopperState({ open: true, task, anchorX: e.clientX, anchorY: e.clientY, submission: sub })
                              }}
                            />
                          )
                        })}

                        {/* "+" bubble — only when expanded, portaled above TimelineHoverLine (z-index 21) */}
                        {features['log_harvest'] && isMainExpanded && isHovered && trackerX !== null && portalTarget && createPortal(
                          <Button
                            variant='text'
                            onClick={(e) => handleBubbleClick(e, task)}
                            onMouseMove={(e) => handleMouseMove(e, task.id, rectX, rectWidth)}
                            onMouseLeave={() => handleMouseLeave(task.id)}
                            sx={{
                              position: 'absolute',
                              left: trackerX - BUBBLE_R + 1,
                              top: y + BUBBLE_TOP_GAP,
                              minWidth: 0,
                              p: 0,
                              height: collapsedRowHeight / 2,
                              zIndex: 21,
                              backgroundColor: taskColor,
                            }}
                          >
                            <ControlPointDuplicate sx={{ fontSize: BUBBLE_R * 2, color: 'white' }} />
                          </Button>,
                          portalTarget
                        )}
                      </g>
                    )
                  })}
                </g>
              )
            })}
          </g>
        )
      })}

      {features['log_harvest'] && popperState?.open && (
        <TimelineTaskSubmissionPopper
          open={popperState.open}
          task={popperState.task}
          anchorX={popperState.anchorX}
          anchorY={popperState.anchorY}
          onClose={handlePopperClose}
          submission={popperState.submission ?? null}
        />
      )}
    </>
  )
})
